// Pure PPE / material fund maths for the GSolar PPE Details tab — no Firestore
// or React imports so it is trivially unit-testable.
//
// Sign convention (single source of truth, used everywhere in the UI):
//   balance = funds charged to the client - material purchased - amounts already settled
//   balance > 0  -> ALM is holding unused client money  -> PAYABLE to the client
//   balance < 0  -> ALM spent more than it was funded   -> RECEIVABLE from the client
// A settlement's `settledAmount` carries the same sign as the balance it clears.

export const PPE_RATE_PER_EMPLOYEE = 950;

// GSolar Power and VS Alpha share one PPE fund and one ledger (stored under G_SOLAR).
export const PPE_COMPANIES = Object.freeze([
  { code: "G_SOLAR", label: "GSolar Power & VS Alpha" }
]);
export const PPE_INVOICE_CLIENT_CODES = Object.freeze(["G_SOLAR", "VS_ALPHA"]);

export const ENTRY_KINDS = Object.freeze({ FUND: "FUND", PURCHASE: "PURCHASE" });
export const PURCHASE_STATUSES = Object.freeze(["REQUESTED", "PURCHASED", "DELIVERED"]);

export function round(value) {
  return Math.round(Number(value || 0));
}

export function isActiveEntry(entry) {
  return entry.entryStatus !== "REJECTED";
}

export function monthsBetween(start, end) {
  const months = [];
  let [year, month] = String(start).split("-").map(Number);
  const [endYear, endMonth] = String(end).split("-").map(Number);
  if (!year || !month || !endYear || !endMonth) return months;
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return months;
}

// Half-years are the reconciliation unit (Jan-Jun / Jul-Dec).
export function halfYearOf(period) {
  const [year, month] = String(period).split("-").map(Number);
  const first = month <= 6;
  return {
    key: `${year}-${first ? "H1" : "H2"}`,
    label: `${first ? "Jan - Jun" : "Jul - Dec"} ${year}`,
    start: `${year}-${first ? "01" : "07"}`,
    end: `${year}-${first ? "06" : "12"}`
  };
}

// Settlement is yearly (calendar year, Jan - Dec).
export function yearOf(period) {
  const year = Number(String(period).split("-")[0]);
  return { key: String(year), label: `Jan - Dec ${year}`, start: `${year}-01`, end: `${year}-12` };
}

export function yearOptions(fromYear, toYear) {
  const options = [];
  for (let year = toYear; year >= fromYear; year -= 1) options.push(yearOf(`${year}-01`));
  return options;
}

export function halfYearOptions(fromYear, toYear) {
  const options = [];
  for (let year = toYear; year >= fromYear; year -= 1) {
    options.push(halfYearOf(`${year}-07`), halfYearOf(`${year}-01`));
  }
  return options;
}

const kindOrder = (entry) => (entry.kind === ENTRY_KINDS.FUND ? 0 : 1);

// Stable ledger order: month, funds before purchases, then date, then the
// original insertion sequence so same-day lines keep the order they were typed.
export function sortLedger(entries) {
  return [...entries].sort((a, b) =>
    String(a.period).localeCompare(String(b.period))
    || kindOrder(a) - kindOrder(b)
    || String(a.entryDate || "9999").localeCompare(String(b.entryDate || "9999"))
    || Number(a.seq || 0) - Number(b.seq || 0)
  );
}

export function sumByKind(entries, kind) {
  return round(entries
    .filter((entry) => isActiveEntry(entry) && entry.kind === kind)
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0));
}

// Balance carried INTO `start`: everything funded/spent before it, less
// anything already settled for periods that ended before it.
export function openingBalance({ entries, settlements, start }) {
  const before = entries.filter((entry) => String(entry.period) < start);
  const settled = settlements
    .filter((settlement) => String(settlement.periodEnd) < start)
    .reduce((sum, settlement) => sum + Number(settlement.settledAmount || 0), 0);
  return round(sumByKind(before, ENTRY_KINDS.FUND) - sumByKind(before, ENTRY_KINDS.PURCHASE) - settled);
}

// What makes up the opening balance: funds and purchases brought forward from
// earlier months, less anything already settled. Unsettled periods simply roll on.
export function broughtForward({ entries, settlements, start }) {
  const before = entries.filter((entry) => String(entry.period) < start);
  const funds = sumByKind(before, ENTRY_KINDS.FUND);
  const purchases = sumByKind(before, ENTRY_KINDS.PURCHASE);
  const settled = round(settlements
    .filter((settlement) => String(settlement.periodEnd) < start)
    .reduce((sum, settlement) => sum + Number(settlement.settledAmount || 0), 0));
  return { funds, purchases, settled, net: round(funds - purchases - settled) };
}

// Position since the last settlement (or since the first entry when nothing has
// been settled yet) — the running balance while a period is left open.
export function runningPosition({ entries, settlements }) {
  const lastSettlement = [...settlements].sort((a, b) => String(a.periodEnd).localeCompare(String(b.periodEnd))).pop() || null;
  const sinceAfter = lastSettlement ? String(lastSettlement.periodEnd) : "";
  const since = entries.filter((entry) => String(entry.period) > sinceAfter);
  const firstMonth = since.map((entry) => entry.period).sort()[0] || "";
  const carried = lastSettlement ? round(lastSettlement.carriedForward) : 0;
  const funds = sumByKind(since, ENTRY_KINDS.FUND);
  const purchases = sumByKind(since, ENTRY_KINDS.PURCHASE);
  return { lastSettlement, firstMonth, carried, funds, purchases, balance: round(carried + funds - purchases) };
}

export function summarizePeriod({ entries, settlements, start, end }) {
  const inPeriod = entries.filter((entry) => String(entry.period) >= start && String(entry.period) <= end);
  const forward = broughtForward({ entries, settlements, start });
  const opening = forward.net;
  const funds = sumByKind(inPeriod, ENTRY_KINDS.FUND);
  const purchases = sumByKind(inPeriod, ENTRY_KINDS.PURCHASE);
  const closing = round(opening + funds - purchases);
  const settlement = settlements.find((item) => item.periodStart === start && item.periodEnd === end) || null;
  const settled = settlement ? round(settlement.settledAmount) : 0;
  return {
    opening, forward, funds, purchases, closing,
    settlement,
    settled,
    carriedForward: round(closing - settled)
  };
}

// Entries for the period with a running balance column. Rejected entries are
// kept in the list (for the audit trail) but never move the balance.
export function ledgerWithRunningBalance({ entries, start, end, opening }) {
  let running = Number(opening || 0);
  return sortLedger(entries.filter((entry) => String(entry.period) >= start && String(entry.period) <= end))
    .map((entry) => {
      if (isActiveEntry(entry)) {
        running += (entry.kind === ENTRY_KINDS.FUND ? 1 : -1) * Number(entry.amount || 0);
      }
      return { ...entry, runningBalance: round(running) };
    });
}

export function describeBalance(balance, clientLabel = "the client") {
  const value = round(balance);
  if (value > 0) return { tone: "success", text: `Payable to ${clientLabel}`, detail: "ALM holds unused PPE funds" };
  if (value < 0) return { tone: "warning", text: `Receivable from ${clientLabel}`, detail: "ALM spent more than it was funded" };
  return { tone: "success", text: "Nil balance", detail: "Funds received equal material purchased" };
}

export function validateSettlement({ closing, settledAmount }) {
  const amount = round(settledAmount);
  if (Number.isNaN(amount)) return "Enter a settlement amount.";
  if (closing === 0 && amount !== 0) return "The balance is nil, so there is nothing to settle.";
  if (amount !== 0 && Math.sign(amount) !== Math.sign(closing)) {
    return closing > 0
      ? "A payable balance must be settled with a positive amount."
      : "A receivable balance must be settled with a negative amount.";
  }
  if (Math.abs(amount) > Math.abs(closing)) return "The settlement cannot be larger than the balance.";
  return "";
}

export function fundFromInvoiceLines(lines) {
  return round((lines || [])
    .filter((line) => line.sourceField === "employees.ppe" || /^ppe\b/i.test(line.description || ""))
    .reduce((sum, line) => sum + Number(line.amount || 0), 0));
}
